// @ts-strict-ignore
import axios from "axios";
import { captureException } from "observability";
import { BASE_URL } from "config/index";
import { OPTIMIZELY_FEATURE_FLAGS } from "analytics/optimizely/config";
import { fetchAssetPrice } from "utils/prices";
import { checkForUSDStableCoin, isCryptoSymbol } from "../utils";
import {
  DEFAULT_QUOTE_CURRENCY,
  MAX_CRYPTO_PRECISION
} from "constants/converter";
import { getCoinGeckoId } from "utils/getCoinGeckoID";
import type { FeatureVariablesType } from "hooks/useFeatureFlags";

type GetCoinGeckoConverterDataType = {
  baseSymbol: string;
  quoteSymbol?: string;
  startTimestampParam?: number;
  latestExchangeTimestamp?: number;
  precision?: number;
  featureVariables: FeatureVariablesType;
};

export const getCoinGeckoConverterData = async ({
  baseSymbol,
  quoteSymbol,
  startTimestampParam,
  latestExchangeTimestamp,
  precision = MAX_CRYPTO_PRECISION,
  featureVariables
}: GetCoinGeckoConverterDataType) => {
  try {
    const startTimestamp = startTimestampParam ? startTimestampParam / 1000 : 0;
    const coinGeckoId = getCoinGeckoId(
      baseSymbol,
      featureVariables[OPTIMIZELY_FEATURE_FLAGS.COINGECKO_TOKEN_MAP]
        .additionalTokenMap
    );
    const quoteSymbolID =
      getCoinGeckoId(
        quoteSymbol,
        featureVariables[OPTIMIZELY_FEATURE_FLAGS.COINGECKO_TOKEN_MAP]
          .additionalTokenMap
      ) || quoteSymbol; // in case the quotesymbol is also a crypto
    // coingecko uses unix ts, exchange uses ms - convert to unix
    const endTimestamp =
      (latestExchangeTimestamp ?? new Date().getTime()) / 1000;
    if (coinGeckoId) {
      const url = `${BASE_URL}/api/coins/${coinGeckoId}/market_chart/range?vs_currency=${quoteSymbolID}&from=${startTimestamp}&to=${endTimestamp}&precision=${precision}`;

      const response = await axios.get(url);
      return response?.data;
    }
    throw new Error("CoinGecko ID not found");
  } catch (e) {
    if (isCryptoSymbol(baseSymbol) && isCryptoSymbol(quoteSymbol)) {
      // When both baseSymbol and quoteSymbol are crypto symbols, we will bridge the prices by fetching the two separate pairs.
      // For example, if baseSymbol is BTC and quoteSymbol is PEPE
      // , we will fetch BTCUSD and PEPEUSD pairs and finally compute the data for the original pair.
      const baseSymbolData = await getCoinGeckoConverterData({
        baseSymbol,
        quoteSymbol: DEFAULT_QUOTE_CURRENCY,
        startTimestampParam,
        latestExchangeTimestamp,
        featureVariables
      });
      const quoteSymbolData = await getCoinGeckoConverterData({
        baseSymbol: quoteSymbol,
        quoteSymbol: DEFAULT_QUOTE_CURRENCY,
        startTimestampParam,
        latestExchangeTimestamp,
        featureVariables
      });

      // compute the exchange rate for the original pair
      const baseSymbolPrice =
        baseSymbolData?.prices?.[baseSymbolData.prices.length - 1]?.[1] ?? 0;
      const quoteSymbolPrice =
        quoteSymbolData?.prices?.[quoteSymbolData.prices.length - 1]?.[1] ?? 0;
      const price = (
        parseFloat(baseSymbolPrice) / parseFloat(quoteSymbolPrice)
      ).toString();
      return {
        ...baseSymbolData,
        prices: [[latestExchangeTimestamp, price]]
      };
    }
    captureException(e as Error);
    throw e;
  }
};

const fetchCoinGeckoRes = async (
  baseSymbol,
  quoteSymbol,
  featureVariables: FeatureVariablesType
) => {
  let price = "0";
  try {
    const coinGeckoRes = await getCoinGeckoConverterData({
      baseSymbol,
      quoteSymbol,
      // Fetch data for the last 1000 seconds
      startTimestampParam: new Date().getTime() - 1000000,
      latestExchangeTimestamp: new Date().getTime(),
      featureVariables
    });

    if (
      Array.isArray(coinGeckoRes?.prices) &&
      coinGeckoRes.prices?.length > 0
    ) {
      price = coinGeckoRes.prices[coinGeckoRes.prices.length - 1][1];
    } else if (isCryptoSymbol(baseSymbol) && isCryptoSymbol(quoteSymbol)) {
      // When both baseSymbol and quoteSymbol are crypto symbols, we will bridge the prices by fetching the two separate pairs.
      // For example, if baseSymbol is BTC and quoteSymbol is PEPE
      // , we will fetch BTCUSD and PEPEUSD pairs and finally compute the exchange rate for BTCPEPE.
      const basePrice = await pairPriceFetcherFunction(
        "",
        baseSymbol,
        DEFAULT_QUOTE_CURRENCY,
        featureVariables
      );
      const quotePrice = await pairPriceFetcherFunction(
        "",
        quoteSymbol,
        DEFAULT_QUOTE_CURRENCY,
        featureVariables
      );
      price = (parseFloat(basePrice) / parseFloat(quotePrice)).toString();
    }
  } catch (error) {
    captureException(error as Error);
  } finally {
    return price;
  }
};
export const pairPriceFetcherFunction = async (
  _,
  baseSymbol,
  quoteSymbol,
  featureVariables: FeatureVariablesType
): Promise<string> => {
  const isUSDPeggedStableCoin = checkForUSDStableCoin(
    quoteSymbol?.toLowerCase()
  );

  const processedCurrencySymbol = isUSDPeggedStableCoin
    ? DEFAULT_QUOTE_CURRENCY
    : quoteSymbol.toLowerCase();

  let price: string;
  // If both the base and quote symbols are USD pegged stable coins, return 1
  if (checkForUSDStableCoin(quoteSymbol) && checkForUSDStableCoin(baseSymbol)) {
    return "1";
  }
  try {
    const assetPriceRes = await fetchAssetPrice(
      baseSymbol,
      "1d",
      processedCurrencySymbol
    );
    const { data } = assetPriceRes;
    if (data.lastTradePrice === "0") {
      return await fetchCoinGeckoRes(
        baseSymbol,
        processedCurrencySymbol,
        featureVariables
      );
    } else {
      price = data.lastTradePrice;
    }
    return price;
  } catch (error) {
    captureException(error as Error);
    return await fetchCoinGeckoRes(
      baseSymbol,
      processedCurrencySymbol,
      featureVariables
    );
  }
};
